import { baseConsumerUrl, basePartnerpageUrl } from 'embed/constants/config'
import { snakeCase, urlSerialize } from 'embed/utils'
import {
  Brand,
  ClientTags,
  ConsumerProductPath,
  FILTER_QUERY_PARAM_PREFIX,
  Filters,
  Product,
  QueryParamKeys,
} from '@evenfinancial/even-ts-static'
import { DeepReadonly } from 'utility-types'
import { TAG_PREFIX } from '../QueryParams'

export interface PartnerpageParams {
  creditScore?: string
  loanAmount?: number
  purpose?: string

  dateOfBirth?: string
  education?: string
  email?: string
  firstName?: string
  lastName?: string

  streetAddress1?: string
  streetAddress2?: string
  zipcode?: string

  employment?: string

  monthlyIncome?: number
  monthlyDebt?: number
  totalAssets?: number

  transactionType?: string
  propertyCounty?: string
  propertyState?: string
  propertySearchStatus?: string
  propertyType?: string
  propertyValue?: number
  downPayment?: number
  refinanceType?: string
  refinanceAmount?: number
  cashOutAmount?: number
  numUnits?: string
  purchaseStatus?: string
  closingDate?: string
  purchaseDate?: string
  occupancyType?: string

  isTest?: boolean
  trackingUuid?: string
}

export type ConsumerPartnerPageParams = { [K in QueryParamKeys]?: unknown }

const cleanParams = (params: PartnerpageParams | ConsumerPartnerPageParams) =>
  Object.keys(params).reduce((acc, key) => {
    if (params[key] && params[key] !== '') acc[key] = params[key]
    return acc
  }, {})

const serializePageParams = (params: PartnerpageParams | ConsumerPartnerPageParams) =>
  Object.fromEntries(Object.entries(cleanParams(params)).map(([key, value]) => [snakeCase(key), value]))

const serializeFilterParams = (params: Filters) =>
  Object.fromEntries(
    Object.entries(cleanParams(params)).map(([key, value]) => [FILTER_QUERY_PARAM_PREFIX + key, value]),
  )

const serializeClientTagParams = (params: DeepReadonly<ClientTags>) => {
  let serializedParams = { ...params }
  serializedParams = Object.fromEntries(
    Object.entries(serializedParams).map(([key, value]) => [TAG_PREFIX + key, value]),
  )

  return Object.fromEntries(
    Object.entries(serializedParams).map((key, value) => [key, Array.isArray(value) ? value[0] : value]),
  )
}

export const buildPartnerpageUrl = (partnerKey: string, pageParams: PartnerpageParams = {}) => {
  const serializedPageParams = serializePageParams(pageParams)

  return `${basePartnerpageUrl}/${partnerKey}/advanced-search/?${urlSerialize(serializedPageParams)}`
}

export const buildConsumerPartnerPageUrl = (
  product: Product | 'Preferred',
  pageParams: ConsumerPartnerPageParams = {},
  filterParams: Filters = {},
  clientTagParams: DeepReadonly<ClientTags> = {},
  partnerKey = '',
) => {
  const serializedPageParams = serializePageParams(pageParams)
  const serializedFilters = serializeFilterParams(filterParams)
  const serializedClientTags = serializeClientTagParams(clientTagParams)

  let url = baseConsumerUrl
  if (partnerKey !== '' && partnerKey !== Brand.Even && partnerKey !== Brand.Fiona && partnerKey !== Brand.LeapLife) {
    url += `/${partnerKey}`
  }
  url += `/${ConsumerProductPath[product]}`
  return `${url}?${urlSerialize(serializedPageParams)}&${urlSerialize(serializedFilters)}&${urlSerialize(
    serializedClientTags,
  )}`
}
